import { Button, Pagination, Select, Table, Tag, Tooltip, notification } from 'antd';
import { PlusOutlined, StopOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';
import { useState } from 'react';
import CustomButton from 'components/custom-button';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { PENALTY_STATUS_OPTIONS, WAIVE_MODE_LABEL, labelOf } from 'pages/star-shared/labels';
import { mapStarError } from 'pages/star-shared/star-error';
import { PenaltyRow, PenaltyStatus } from 'pages/star-shared/types';
import { TABLE_EMPTY, formatDateTime, personName } from 'pages/star-shared/utils';
import { usePagedList } from 'pages/star-shared/use-paged-list';
import StarAmount from 'pages/star-shared/components/star-amount';
import {
  Ellipsis,
  NoWrap,
  PenaltyStatusTag,
  UserCell,
} from 'pages/star-shared/components/status-tags';
import UserPicker, { UserPickerValue } from 'pages/star-shared/components/user-picker';
import NoteActionModal, { NoteActionConfig } from 'pages/star-shared/components/note-action-modal';
import RecordPenaltyModal from './record-penalty-modal';
import { colors } from 'theme/tokens';

const LIMIT = 20;

const RecordsTab = () => {
  const [status, setStatus] = useState<PenaltyStatus | undefined>(undefined);
  const [user, setUser] = useState<UserPickerValue>(undefined);
  const [page, setPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);
  const [recordOpen, setRecordOpen] = useState(false);
  const [action, setAction] = useState<NoteActionConfig | null>(null);
  const { items, total, loading } = usePagedList<PenaltyRow>(
    API.starsPenalties,
    { status, userId: user?.value, page, limit: LIMIT },
    { refreshKey }
  );
  const refresh = () => setRefreshKey((k) => k + 1);

  const openCancel = (p: PenaltyRow) => {
    setAction({
      title: 'Huỷ bản ghi phạt',
      description: (
        <span>
          #{p.id} · {personName(p.user)} · {p.typeName} · <StarAmount value={p.starCost} />
          {p.status === 'WAIVE_PENDING' && (
            <div style={{ color: colors.textSecondary, marginTop: 4 }}>
              Bản ghi đang chờ duyệt xoá; huỷ sẽ hoàn Sao đã trừ (nếu có).
            </div>
          )}
        </span>
      ),
      noteLabel: 'Lý do huỷ (không bắt buộc)',
      confirmText: 'Huỷ bản ghi',
      danger: true,
      onSubmit: async (note) => {
        try {
          await AxiosUtils.patch(`${API.starsPenalties}/${p.id}/cancel`, note ? { note } : {});
          notification.success({ message: `Đã huỷ bản ghi phạt #${p.id}.` });
          refresh();
        } catch (err) {
          notification.error({ message: mapStarError(err) });
          throw err;
        }
      },
    });
  };

  const columns: ColumnsType<PenaltyRow> = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 70 },
    { title: 'Người bị phạt', key: 'user', render: (_, p) => <UserCell user={p.user} /> },
    {
      title: 'Loại phạt',
      key: 'type',
      render: (_, p) => (
        <div>
          <div style={{ fontWeight: 600 }}>{p.typeName}</div>
          <Tag color={p.waiveMode === 'REVIEW' ? 'gold' : 'blue'}>
            {labelOf(WAIVE_MODE_LABEL, p.waiveMode)}
          </Tag>
        </div>
      ),
    },
    {
      title: 'Sao để xoá',
      key: 'starCost',
      width: 110,
      align: 'right',
      render: (_, p) => <StarAmount value={p.starCost} />,
    },
    { title: 'Ghi chú', key: 'note', render: (_, p) => <Ellipsis value={p.note} width={200} /> },
    {
      title: 'Trạng thái',
      key: 'status',
      width: 130,
      render: (_, p) => <PenaltyStatusTag value={p.status} />,
    },
    {
      title: 'Ghi lúc',
      key: 'recordedAt',
      width: 190,
      render: (_, p) => (
        <div>
          <NoWrap>{formatDateTime(p.recordedAt)}</NoWrap>
          {p.recordedBy && (
            <div style={{ color: colors.textSecondary, fontSize: 12 }}>
              bởi {personName(p.recordedBy)}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Xoá phạt',
      key: 'review',
      width: 220,
      render: (_, p) =>
        p.waiveRequestedAt || p.reviewedAt ? (
          <div>
            {p.waiveRequestedAt && (
              <div>
                Xin xoá: <NoWrap>{formatDateTime(p.waiveRequestedAt)}</NoWrap>
              </div>
            )}
            {p.reviewedAt && (
              <div>
                Duyệt: <NoWrap>{formatDateTime(p.reviewedAt)}</NoWrap>
                {p.reviewedBy ? ` · ${personName(p.reviewedBy)}` : ''}
              </div>
            )}
            <Ellipsis value={p.reviewNote} width={200} />
          </div>
        ) : (
          '—'
        ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      fixed: 'right' as const,
      width: 70,
      render: (_, p) =>
        p.status === 'OPEN' || p.status === 'WAIVE_PENDING' ? (
          <Tooltip title="Huỷ bản ghi phạt">
            <Button danger type="text" icon={<StopOutlined />} onClick={() => openCancel(p)} />
          </Tooltip>
        ) : null,
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Select
          allowClear
          placeholder="Mọi trạng thái"
          style={{ width: 170 }}
          options={PENALTY_STATUS_OPTIONS}
          value={status}
          onChange={(value) => {
            setStatus(value);
            setPage(1);
          }}
        />
        <div style={{ width: 300 }}>
          <UserPicker
            value={user}
            onChange={(value) => {
              setUser(value);
              setPage(1);
            }}
            placeholder="Lọc theo người"
          />
        </div>
        <div style={{ flex: 1 }} />
        <CustomButton
          bgColor="primary-red"
          text="Ghi phạt"
          icon={<PlusOutlined />}
          onClick={() => setRecordOpen(true)}
        />
      </div>
      <div className="custom-table">
        <Table
          columns={columns}
          rowKey="id"
          pagination={false}
          dataSource={items}
          loading={loading}
          locale={TABLE_EMPTY}
          scroll={{ x: 1300 }}
        />
        {total > LIMIT && (
          <Pagination
            showSizeChanger={false}
            current={page}
            onChange={setPage}
            showLessItems
            total={total}
            pageSize={LIMIT}
          />
        )}
      </div>
      <RecordPenaltyModal
        open={recordOpen}
        onClose={() => setRecordOpen(false)}
        onSuccess={refresh}
      />
      <NoteActionModal config={action} onClose={() => setAction(null)} />
    </div>
  );
};

export default RecordsTab;
